import React, { useState, useContext, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import AuthContext from "../../context/AuthContext";
import { AlertCircle, CheckCircle, X, Save } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import usersService from "../../services/usersService";
import { formatError } from "../../utils/errorHandler";
import { useTranslation } from "../../i18n";

const UserInformation = () => {
  const { t } = useTranslation("settings");
  const { user, isInitialized, refreshUser } = useContext(AuthContext);
  const [editMode, setEditMode] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    full_name: "",
    email: ""
  });
  const [formErrors, setFormErrors] = useState({});
  const queryClient = useQueryClient();

  // Sync form data with user data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || "",
        email: user.email || ""
      });
    }
  }, [user]);

  const [hideBirthday, setHideBirthday] = useState(false);
  useEffect(() => {
    if (user) setHideBirthday(!!user.hide_birthday);
  }, [user]);

  const hideBirthdayMutation = useMutation({
    mutationFn: (next) => usersService.updateCurrentUser({ hide_birthday: next }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      if (refreshUser) refreshUser();
    },
    onError: (err) => {
      setError(formatError(err));
      setHideBirthday(!!user?.hide_birthday);
    },
  });

  const handleHideBirthdayToggle = () => {
    const next = !hideBirthday;
    setHideBirthday(next);
    hideBirthdayMutation.mutate(next);
  };

  // The leaderboard widget tells people to change this here, so here it is.
  const [hideFromLeaderboard, setHideFromLeaderboard] = useState(false);
  useEffect(() => {
    if (user) setHideFromLeaderboard(!!user.hide_from_leaderboard);
  }, [user]);

  const hideFromLeaderboardMutation = useMutation({
    mutationFn: (next) =>
      usersService.updateCurrentUser({ hide_from_leaderboard: next }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      if (refreshUser) refreshUser();
    },
    onError: (err) => {
      setError(formatError(err));
      setHideFromLeaderboard(!!user?.hide_from_leaderboard);
    },
  });

  const handleHideFromLeaderboardToggle = () => {
    const next = !hideFromLeaderboard;
    setHideFromLeaderboard(next);
    hideFromLeaderboardMutation.mutate(next);
  };

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data) => usersService.updateCurrentUser(data),
    onSuccess: () => {
      setSuccess(true);
      setError("");
      setEditMode(false);
      // Invalidate queries to refetch user data
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      // Refresh user in auth context
      if (refreshUser) refreshUser();
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    },
    onError: (err) => {
      setError(formatError(err));
      setSuccess(false);
    },
  });

  const validateForm = () => {
    const errors = {};
    if (!formData.full_name.trim()) {
      errors.full_name = t("profile.errors.full_name_required");
    } else if (formData.full_name.length > 100) {
      errors.full_name = t("profile.errors.full_name_too_long");
    }
    if (!formData.email.trim()) {
      errors.email = t("profile.errors.email_required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t("profile.errors.email_invalid");
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      updateProfileMutation.mutate(formData);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Edit mode handler (kept for future use)
  // eslint-disable-next-line no-unused-vars
  const handleEditClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditMode(true);
    setError("");
    setFormErrors({});
  };

  const handleCancel = (e) => {
    e.preventDefault();
    // Reset form data to original user values
    setFormData({
      full_name: user?.full_name || "",
      email: user?.email || ""
    });
    setError("");
    setFormErrors({});
    setEditMode(false);
  };

  if (!isInitialized || !user) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full"></div>
        <span className="ml-2 text-gray-600">{t("common:status.loading")}</span>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">{t("profile.title")}</h2>
      </div>

      {/* Success Message */}
      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 p-3 bg-green-50 border border-green-200 rounded-2xl flex items-center text-green-700"
          >
            <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <p className="text-sm">{t("profile.updated")}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 p-3 bg-red-50 border border-red-200 rounded-2xl flex items-center text-red-700"
          >
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {editMode ? (
        /* Edit Mode - Show Form */
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Full Name Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("profile.full_name")}
            </label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleInputChange}
              placeholder={t("profile.full_name_placeholder")}
              className={`w-full px-4 py-3 border rounded-full focus:outline-none focus:ring-2 transition-all ${
                formErrors.full_name
                  ? "border-red-300 focus:ring-red-500 bg-red-50"
                  : "border-gray-300 focus:ring-purple-500 focus:border-purple-500"
              }`}
            />
            {formErrors.full_name && (
              <p className="mt-1 text-xs text-red-600">{formErrors.full_name}</p>
            )}
          </div>

          {/* Email Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("profile.email")}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t("profile.email_placeholder")}
              className={`w-full px-4 py-3 border rounded-full focus:outline-none focus:ring-2 transition-all ${
                formErrors.email
                  ? "border-red-300 focus:ring-red-500 bg-red-50"
                  : "border-gray-300 focus:ring-purple-500 focus:border-purple-500"
              }`}
            />
            {formErrors.email && (
              <p className="mt-1 text-xs text-red-600">{formErrors.email}</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={updateProfileMutation.isPending}
              className="flex items-center gap-2 px-6 py-2.5 org-gradient text-white rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            >
              <Save className="w-4 h-4" />
              {updateProfileMutation.isPending
                ? t("common:actions.saving")
                : t("common:actions.save_changes")}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={updateProfileMutation.isPending}
              className="flex items-center gap-2 px-6 py-2.5 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <X className="w-4 h-4" />
              {t("common:actions.cancel")}
            </button>
          </div>
        </form>
      ) : (
        /* View Mode - Show User Info */
        <div className="space-y-6">
          {/* Full Name Display */}
          <div className="p-4 bg-gray-50 rounded-2xl">
            <label className="block text-sm font-medium text-gray-500 mb-1">
              {t("profile.full_name")}
            </label>
            <p className="text-lg text-gray-900 font-medium">
              {user.full_name || <span className="text-gray-400 italic">{t("profile.not_set")}</span>}
            </p>
          </div>

          {/* Email Display */}
          <div className="p-4 bg-gray-50 rounded-2xl">
            <label className="block text-sm font-medium text-gray-500 mb-1">
              {t("profile.email")}
            </label>
            <p className="text-lg text-gray-900 font-medium">{user.email}</p>
          </div>

          {/* Additional Info */}
          {(user.department || user.job_title || user.location) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {user.job_title && (
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    {t("profile.job_title")}
                  </label>
                  <p className="text-gray-900">{user.job_title}</p>
                </div>
              )}
              {user.department && (
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    {t("profile.department")}
                  </label>
                  <p className="text-gray-900">{user.department}</p>
                </div>
              )}
              {user.location && (
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    {t("profile.location")}
                  </label>
                  <p className="text-gray-900">{user.location}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Privacy */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">{t("profile.privacy.title")}</h3>
        <div className="flex items-center justify-between p-4 rounded-2xl border border-gray-200 bg-white">
          <div>
            <p className="font-medium text-gray-900">{t("profile.privacy.hide_birthday")}</p>
            <p className="text-sm text-gray-500 mt-0.5">
              {t("profile.privacy.hide_birthday_help")}
            </p>
          </div>
          <button
            type="button"
            onClick={handleHideBirthdayToggle}
            disabled={hideBirthdayMutation.isPending}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 flex-shrink-0 ml-4 ${
              hideBirthday ? "bg-purple-600" : "bg-gray-300"
            }`}
            aria-pressed={hideBirthday}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                hideBirthday ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        <div className="mt-3 flex items-center justify-between p-4 rounded-2xl border border-gray-200 bg-white">
          <div>
            <p className="font-medium text-gray-900">
              {t("profile.privacy.hide_from_leaderboard")}
            </p>
            <p className="text-sm text-gray-500 mt-0.5">
              {t("profile.privacy.hide_from_leaderboard_help")}
            </p>
          </div>
          <button
            type="button"
            onClick={handleHideFromLeaderboardToggle}
            disabled={hideFromLeaderboardMutation.isPending}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 flex-shrink-0 ml-4 ${
              hideFromLeaderboard ? "bg-purple-600" : "bg-gray-300"
            }`}
            aria-pressed={hideFromLeaderboard}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                hideFromLeaderboard ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserInformation;
